import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, signal, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Location } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { AIScreeningService } from '../../../services/ai-Screening';
import { finalize } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-ai-interview-performance',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './ai-interview-performance.html',
  styleUrl: './ai-interview-performance.css'
})
export class AiInterviewPerformance implements OnInit, AfterViewInit {
  @ViewChild('lightboxVideo') lightboxVideo?: ElementRef<HTMLVideoElement>;
  @ViewChild('videoContainer') videoContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLDivElement>;

  isTeacherRole: boolean = false;
  isAudioCollapsed: boolean = false;

  private isManualToggle = false;
  private autoExpandTimeout: any;
  private hoverTimeout: any;

  sectionsList = ['performance', 'breakdown', 'integrity', 'timeline', 'audio', 'recording', 'questions'];
  activeSection = 'performance';
  isB2cPractice = false;

  collapsedSections: { [key: string]: boolean } = {
    performance: true,
    breakdown: true,
    integrity: true,
    timeline: true,
    audio: true,
    recording: true,
    questions: true
  };

  alertLogs: any[] = [];
  evaluationData?: any;
  errorMessage = '';

  totalScore = 0;
  feedbackLabel = '';
  role = '';
  company = '';
  round = '';
  duration = 0;
  startTime = '';
  endTime = '';
  voiceAnalysisCards: any[] = [];
  currentCandidateId: number | string = '';
  currentSessionId: number | string = '';
  currentToken: string | null = null;
  currentAttemptNumber = 1;
  totalAttempts = 1;
  attemptsList: any[] = [];

  // UI States
  performanceData: any[] = [];
  teacherPerformanceData: any[] = [];
  selectedQuestion = 0;
  strengths: string[] = [];
  skill_gaps: string[] = [];
  nextSteps: { color: string; text: string }[] = [];
  questions: any[] = [];
  isInterviewIncomplete = false;
  completenessMessage = '';
  isLoading: boolean = true;
  selectedImage = signal<string | null>(null);
  screenshots: any[] = [];
  violationReport: any = null;
  screenRecordUrl: string | null = null;
  selectedVideo = signal<SafeResourceUrl | null>(null);
  voiceAnalysis?: any;
  selectedSeverityFilter = 'ALL';

  get alertStats() {
    return {
      critical: this.alertLogs.filter(l => l.severity === 'CRITICAL').length,
      warnings: this.alertLogs.filter(l => l.severity === 'WARNING').length,
      info: this.alertLogs.filter(l => l.severity === 'INFO').length
    };
  }

  currentTime = signal<number>(0);
  videoDuration = signal<number>(0);
  isDraggingSlider = false;

  // Custom Controls State
  isPlaying = signal<boolean>(false);
  isBuffering = signal<boolean>(false);
  showControls = true;
  private wasPlayingBeforeSeek = false;

  // Audio Controls State
  volume = signal<number>(1);
  isMuted = signal<boolean>(false);
  activeTab = 'history';

  get integerTotalScore(): number {
    return Math.trunc(this.totalScore || 0);
  }

  constructor(
    private location: Location,
    private toast: ToastrService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private aiscreeningService: AIScreeningService,
    private router:Router
  ) { }

  ngOnInit() {
    const candidateIdParam = this.route.snapshot.paramMap.get('candidateId');
    const sessionIdParam = this.route.snapshot.paramMap.get('sessionId');
    const token = this.route.snapshot.paramMap.get('token');
    this.activeTab = this.route.snapshot.queryParamMap.get('tab') || '';

    const candidateId = candidateIdParam ? (isNaN(Number(candidateIdParam)) ? candidateIdParam : Number(candidateIdParam)) : null as any;
    const sessionId = sessionIdParam ? (isNaN(Number(sessionIdParam)) ? sessionIdParam : Number(sessionIdParam)) : null as any;

    this.currentCandidateId = candidateId;
    this.currentSessionId = sessionId;
    this.currentToken = token;

    this.loadEvaluation(candidateId, sessionId, token);
  }

  ngAfterViewInit() {
  }

  onSectionHover(sectionName: string) {
    if (this.activeSection === sectionName) return;
    if (!this.collapsedSections[sectionName]) return; // Already temporarily open

    clearTimeout(this.hoverTimeout);

    this.hoverTimeout = setTimeout(() => {
      this.collapsedSections[sectionName] = false;
      this.cdr.detectChanges();
    }, 150);
  }

  onSectionLeave(sectionName: string) {
    clearTimeout(this.hoverTimeout);

    if (this.activeSection !== sectionName && !this.collapsedSections[sectionName]) {
      this.collapsedSections[sectionName] = true;
      this.cdr.detectChanges();
    }
  }

  onScroll() {
    if (this.isManualToggle) return;

    let closestSection = '';
    const threshold = window.innerHeight * 0.4;
    const sectionsReversed = [...this.sectionsList].reverse();

    for (const sec of sectionsReversed) {
      const el = document.getElementById('section-' + sec);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= threshold) {
          closestSection = sec;
          break;
        }
      }
    }

    if (closestSection && this.activeSection !== closestSection) {
      this.activeSection = closestSection;
      this.expandSectionOnScroll(closestSection);
    }
  }

  expandSectionOnScroll(sectionName: string) {
    if (this.isManualToggle) return;

    let changed = false;
    Object.keys(this.collapsedSections).forEach(key => {
      if (key === sectionName) {
        if (this.collapsedSections[key] !== false) {
          this.collapsedSections[key] = false;
          changed = true;
        }
      } else {
        if (this.collapsedSections[key] !== true) {
          this.collapsedSections[key] = true;
          changed = true;
        }
      }
    });

    if (changed) {
      this.cdr.detectChanges();
    }
  }

 
  goBack() {
  const tab = this.route.snapshot.queryParamMap.get('tab');
  if (tab === 'history') {
    this.router.navigate(['/student-dashboard/ai-interview-dashboard'], { queryParams: {  tab: 'history' } } );
    return;
  }
    this.location.back();
  }

  toggleSection(section: string) {
    this.isManualToggle = true;

    if (this.activeSection === section && !this.collapsedSections[section]) {
      this.collapsedSections[section] = true;
      this.activeSection = '';
    } else {
      Object.keys(this.collapsedSections).forEach(key => {
        this.collapsedSections[key] = (key !== section);
      });
      this.collapsedSections[section] = false;
      this.activeSection = section;

      setTimeout(() => {
        const el = document.getElementById('section-' + section);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }

    clearTimeout(this.autoExpandTimeout);
    this.autoExpandTimeout = setTimeout(() => {
      this.isManualToggle = false;
    }, 1500);
  }
  loadEvaluation(candidateId: number | string, sessionId: number | string, tokenFromPath?: string | null) {
    const token = tokenFromPath;
    this.aiscreeningService.getInterviewReport(candidateId, sessionId, token).pipe(finalize(() => {
      this.isLoading = false;
      this.cdr.detectChanges();
    })).subscribe({
      next: (res: any) => {
        const payload = res.Data ?? res.data;
        const isSuccess = res.Success ?? res.success;
        const message = res.Message ?? res.message;

        if (isSuccess && payload) {
          this.evaluationData = payload;
          this.toast.success('Performance report loaded!');

          if (this.evaluationData) {
            this.mapEvaluationData(this.evaluationData);
          } else {
            this.errorMessage = message || 'Could not load result.';
            this.toast.warning(this.errorMessage);
          }
        } else {
          this.errorMessage = message || 'Failed to load report data from the server.';
          this.toast.warning(this.errorMessage);
        }
      },
      error: (err: any) => {
        this.toast.error('Failed to fetch evaluation. Please try again.');
      }
    })
  }

  private mapEvaluationData(data: any) {

    this.isB2cPractice = data.is_b2c_practice === true
      || this.route.snapshot.queryParamMap.get('is_b2c_practice') === 'true'
      || sessionStorage.getItem('is_b2c_practice') === 'true'
      || (data.front_end_session_id ? sessionStorage.getItem(`is_b2c_practice_${data.front_end_session_id}`) === 'true' : false)
      || (data.session_id ? sessionStorage.getItem(`is_b2c_practice_${data.session_id}`) === 'true' : false);

    if (this.isB2cPractice) {
      this.sectionsList = ['performance', 'breakdown', 'audio', 'questions'];
    }

    this.totalScore = data.total_score;
    this.currentAttemptNumber = data.attempt_number || 1;
    this.totalAttempts = data.total_attempts || 1;
    this.attemptsList = Array.isArray(data.attempts_list) ? data.attempts_list : [];
    if (data.session_id) {
      this.currentSessionId = data.session_id;
    }
    this.role = data.interview_context.role;
    this.company = data.interview_context.company || '';
    this.round = data.interview_context.interview_round;
    this.duration = data.interview_context.duration_minutes;
    this.startTime = data.interview_started_at || '';
    this.endTime = data.interview_ended_at || '';
    this.completenessMessage = data.interview_context.completeness_message || '';

    this.feedbackLabel = data.overall_evaluation.feedback_label;

    /* -------------------- Completeness Detection -------------------- */
    this.isInterviewIncomplete = data.interview_context.completeness_status?.toLowerCase() === 'incomplete';

    /* -------------------- Violation Report -------------------- */
    if (data.violation_report) {
      this.violationReport = {
        isFlagged: data.violation_report.is_flagged,
        total: data.violation_report.total_violations,
        allowed: data.violation_report.allowed_violation_count,
        breakdown: data.violation_report.breakdown,
        events: data.violation_report.events.map((e: any) => ({
          ...e,
          formattedTime: new Date(e.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }),
          videoTime: e.video_timestamp || null,
          formattedDate: new Date(e.timestamp).toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' })
        }))
      };
    }

    /* -------------------- Map Snapshots & Alert Logs -------------------- */
    if (data.snapshots && data.snapshots.length > 0) {
      this.screenshots = data.snapshots.map((s: any) => {
        const d = new Date(s.captured_at);
        return {
          url: s.url,
          time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }),
          videoTime: s.video_timestamp || null,
          date: d.toISOString().split('T')[0],
          type: s.violation_type || 'SNAPSHOT',
          severity: s.severity || 'INFO',
          reason: s.reason || 'Routine verification',
          isViolation: s.is_violation,
          count: s.violation_count
        };
      });

      // Populate Alert Logs from Snapshots (Rich Event Data)
      this.alertLogs = data.snapshots.map((s: any) => {
        const d = new Date(s.captured_at);
        return {
          time: s.video_timestamp || d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
          videoTime: s.video_timestamp || null,
          url: s.url || null,
          date: d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' }),
          event: s.violation_type || 'SNAPSHOT',
          severity: s.severity || 'INFO',
          reason: s.reason || this.getDefaultDescription(s.violation_type || '')
        };
      });
    } else {
      this.screenshots = [];
      this.alertLogs = [];
    }

    const rawRec = data.recording;
    this.screenRecordUrl = typeof rawRec === 'string' ? rawRec : (rawRec?.url ?? null);

    /* -------------------- ZERO SCORE (Interview Incomplete) -------------------- */
    if (this.isInterviewIncomplete) {
      // Optional: Add a custom message to focus areas if incomplete, but don't clear data
      if (!data.overall_evaluation.skill_gaps?.length) {
        this.skill_gaps = [
          this.completenessMessage || 'Complete the full interview to receive a proper evaluation',
          'Ensure you are prepared before starting the interview',
          'Allocate sufficient uninterrupted time for the interview session',
        ];
      }
    }

    this.isTeacherRole = !!data.is_teacher;

    // Performance breakdown
    const perf = data.overall_evaluation.performance_breakdown;
    if (perf) {
      this.performanceData = [
        {
          title: 'Communication',
          subtitle: perf.communication?.description || 'Clarity and articulation',
          score: perf.communication?.score || 0,
          icon: 'bi-mic',
          rating: this.getRatingByScore(perf.communication?.score || 0)
        },
        {
          title: this.isTeacherRole ? 'Subject Knowledge' : 'Technical Knowledge',
          subtitle: (this.isTeacherRole ? perf.subject_knowledge?.description : perf.technical_knowledge?.description) || 'Subject matter expertise',
          score: (this.isTeacherRole ? perf.subject_knowledge?.score : perf.technical_knowledge?.score) || 0,
          icon: 'bi-cpu',
          rating: this.getRatingByScore((this.isTeacherRole ? perf.subject_knowledge?.score : perf.technical_knowledge?.score) || 0)
        },
        {
          title: 'Confidence',
          subtitle: perf.confidence?.description || 'Self-assurance and poise',
          score: perf.confidence?.score || 0,
          icon: 'bi-stars',
          rating: this.getRatingByScore(perf.confidence?.score || 0)
        },
        {
          title: 'Structure',
          subtitle: perf.structure?.description || 'Organized responses',
          score: perf.structure?.score || 0,
          icon: 'bi-diagram-3',
          rating: this.getRatingByScore(perf.structure?.score || 0)
        }
      ];

      this.teacherPerformanceData = [
        {
          title: 'Explanation Skills',
          subtitle: perf.explanation_skills?.description || 'Clear, structured answers with effective use of analogies.',
          score: perf.explanation_skills?.score || 0,
          icon: 'bi-book',
          rating: this.getRatingByScore(perf.explanation_skills?.score || 0)
        },
        {
          title: 'Student Engagement',
          subtitle: perf.student_engagement?.description || 'Solid strategies but limited examples of real classroom use.',
          score: perf.student_engagement?.score || 0,
          icon: 'bi-people',
          rating: this.getRatingByScore(perf.student_engagement?.score || 0)
        },
        {
          title: 'Lesson Structuring',
          subtitle: perf.lesson_structuring?.description || 'Well-paced progression with clear learning objectives.',
          score: perf.lesson_structuring?.score || 0,
          icon: 'bi-mortarboard',
          rating: this.getRatingByScore(perf.lesson_structuring?.score || 0)
        },
        {
          title: 'Concept Clarity',
          subtitle: perf.concept_clarity?.description || 'Strong command of subject and ability to simplify ideas.',
          score: perf.concept_clarity?.score || 0,
          icon: 'bi-lightbulb',
          rating: this.getRatingByScore(perf.concept_clarity?.score || 0)
        }
      ];
    }

    // Questions
    if (data.questions) {
      this.questions = data.questions.map((q: any) => ({
        question: q.question,
        score: q.score,
        answer: q.user_answer,
        good: q.what_went_well,
        skill_gaps: q.skill_gaps,
        tip: 'Reflect on detailed feedback for improvement.',
        better: `Score: ${q.score}/10 - ${q.feedback_label}`
      }));
    }

    // Voice Analysis
    this.voiceAnalysis = data.voice_analysis;
    this.voiceAnalysisCards = [
      {
        title: 'Vocal Delivery',
        score: data.voice_analysis?.vocal_delivery?.score || 0,
        feedback: data.voice_analysis?.vocal_delivery?.feedback || 'Stable pace and clear articulation throughout the session.',
        icon: 'bi-mic-fill',
        color: 'purple'
      },
      {
        title: 'Expression Clarity',
        score: data.voice_analysis?.expression_clarity?.score || 0,
        feedback: data.voice_analysis?.expression_clarity?.feedback || 'Thoughts were expressed clearly with good vocabulary usage.',
        icon: 'bi-translate',
        color: 'blue'
      },
      {
        title: 'Structured Comm.',
        score: data.voice_analysis?.structured_communication?.score || 0,
        feedback: data.voice_analysis?.structured_communication?.feedback || 'Answers followed a logical progression with clear transitions.',
        icon: 'bi-diagram-3-fill',
        color: 'green'
      },
      // Educator-only dimension: only shown when the field is present in the analysis
      ...(data.voice_analysis?.instructional_clarity ? [{
        title: 'Instructional Clarity',
        score: data.voice_analysis.instructional_clarity.score || 0,
        feedback: data.voice_analysis.instructional_clarity.feedback || 'Teaching concepts were explained with clarity and logical structure.',
        icon: 'bi-mortarboard-fill',
        color: 'orange'
      }] : [])
    ];

    // Summary
    this.strengths = data.overall_evaluation.key_strengths || [];
    this.skill_gaps = data.overall_evaluation.skill_gaps || this.skill_gaps;

    // Recommended next steps
    const rawNextSteps = data.overall_evaluation.hiring_recommendation || [];
    const colors = ['purple', 'blue', 'green', 'indigo', 'orange'];

    if (rawNextSteps.length > 0) {
      this.nextSteps = rawNextSteps.map((text: any, i: number) => ({
        color: colors[i % colors.length],
        text: text
      }));
    } else {
      // Fallback defaults
      this.nextSteps = [
        { color: 'purple', text: 'Practice with AI mock interviews regularly.' },
        { color: 'blue', text: 'Focus on technical depth in answers.' },
        { color: 'green', text: 'Review top feedback points and retry next week.' }
      ];
    }

    // Snapshots are already mapped above (before the incomplete check)
  }

  selectQuestion(index: number) {
    this.selectedQuestion = index;
  }

  switchAttempt(attempt: any) {
    if (!attempt || !attempt.session_id) return;
    if (attempt.attempt_number === this.currentAttemptNumber && String(attempt.session_id) === String(this.currentSessionId)) {
      return;
    }
    const candId = this.currentCandidateId || this.route.snapshot.paramMap.get('candidateId') || '';
    const tok = attempt.token || this.currentToken || this.route.snapshot.paramMap.get('token') || 'none';
    this.currentSessionId = attempt.session_id;
    this.currentAttemptNumber = attempt.attempt_number;
    this.isLoading = true;
    this.router.navigate(['/ai-performance-report', candId, attempt.session_id, tok], {
      replaceUrl: true,
      queryParamsHandling: 'preserve'
    });
    this.loadEvaluation(candId, attempt.session_id, tok);
  }

  shareReport() {
    const candidateId = this.currentCandidateId || this.route.snapshot.paramMap.get('candidateId');
    const sessionId = this.currentSessionId || this.route.snapshot.paramMap.get('sessionId');
    const token = this.currentToken || this.route.snapshot.paramMap.get('token');

    if (!candidateId || !sessionId) {
      this.toast.error('Invalid report data. Cannot share.');
      return;
    }

    const baseUrl = window.location.origin;
    const publicLink = `${baseUrl}/ai-performance-report/${candidateId}/${sessionId}/${token}`;

    navigator.clipboard.writeText(publicLink).then(() => {
      this.toast.success('Public share link copied to clipboard!');
    }).catch(err => {
      console.error('Share error:', err);
      this.toast.error('Failed to copy share link.');
    });
  }

  exportPDF() {
    window.print();
  }

  getScoreClass(score: number) {
    if (score >= 80) return 'excellent';
    if (score >= 50) return 'average';
    return 'poor';
  }

  getRatingByScore(score: number): string {
    if (score >= 8.5) return 'excellent';
    if (score >= 7) return 'good';
    if (score >= 5) return 'decent';
    return 'needs-improvement';
  }

  getRatingText(score: number): string {
    if (score >= 80) return 'Excellent';
    if (score >= 50) return 'Good';
    return 'Needs Improvement';
  }

  getQuestionScoreClass(score: number) {
    if (score >= 8.5) return 'excellent';
    if (score >= 7) return 'good';
    if (score >= 5) return 'average';
    return 'poor';
  }

  getQuestionRatingText(score: number): string {
    if (score >= 8.5) return 'Excellent';
    if (score >= 7) return 'Good';
    if (score >= 5) return 'Average';
    return 'Needs Improvement';
  }

  getTimeOnly(dateTimeStr: string): string {
    if (!dateTimeStr) return '--:--:--';
    const parts = dateTimeStr.split(' ');
    if (parts.length >= 2) {
      const timeStr = parts[1];
      const ampm = parts[2] || '';
      return `${timeStr} ${ampm}`;
    }
    return dateTimeStr;
  }

  getDefaultDescription(type: string): string {
    switch (type.toUpperCase()) {
      case 'SESSION_START': return 'Candidate joined the interview session and identity was verified successfully.';
      case 'TAB_SWITCH': return 'Candidate switched away from the interview tab. Strike recorded.';
      case 'FACE_NOT_DETECTED': return 'Face was not detected in the webcam frame for several seconds.';
      case 'MULTIPLE_VOICES': return 'Background voice detected during the response.';
      case 'SESSION_END': return 'Interview session completed and recording was saved.';
      default: return 'Detected by AI proctoring engine';
    }
  }


  get safeRecordingUrl(): SafeResourceUrl | null {
    if (!this.screenRecordUrl) return null;
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.screenRecordUrl);
  }

  openLightbox(url: string) {
    this.selectedImage.set(url);
  }

  closeLightbox() {
    this.selectedImage.set(null);
  }

  openVideoLightbox() {
    if (this.safeRecordingUrl) {
      this.selectedVideo.set(this.safeRecordingUrl);
      this.isPlaying.set(true);
      // Reset time for new view if needed, or keep current
      this.currentTime.set(0);
    }
  }

  closeVideoLightbox() {
    this.selectedVideo.set(null);
    this.isPlaying.set(false);
  }

  seekVideo(seconds: number) {
    if (this.lightboxVideo && this.lightboxVideo.nativeElement) {
      const video = this.lightboxVideo.nativeElement;
      video.currentTime = Math.max(0, Math.min(video.duration || 0, video.currentTime + seconds));
    }
  }

  toggleFullScreen() {
    if (!this.videoContainer) return;

    const elem = this.videoContainer.nativeElement as any;

    if (!document.fullscreenElement) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
    }
  }

  togglePlayPause() {
    if (!this.lightboxVideo?.nativeElement) return;
    const video = this.lightboxVideo.nativeElement;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  }

  toggleMute() {
    this.isMuted.set(!this.isMuted());
    if (this.lightboxVideo?.nativeElement) {
      this.lightboxVideo.nativeElement.muted = this.isMuted();
      if (!this.isMuted() && this.volume() === 0) {
        this.volume.set(0.5);
        this.lightboxVideo.nativeElement.volume = 0.5;
      }
    }
  }

  onVolumeChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const vol = parseFloat(input.value);
    this.volume.set(vol);
    if (this.lightboxVideo?.nativeElement) {
      this.lightboxVideo.nativeElement.volume = vol;
      const isNowMuted = vol === 0;
      this.isMuted.set(isNowMuted);
      this.lightboxVideo.nativeElement.muted = isNowMuted;
    }
  }

  formatTime(seconds: number): string {
    if (seconds === null || seconds === undefined || isNaN(seconds) || !isFinite(seconds)) {
      return '00:00';
    }
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    if (h > 0) {
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  onTimeUpdate(event: Event) {
    if (!this.isDraggingSlider) {
      const video = event.target as HTMLVideoElement;
      this.currentTime.set(video.currentTime);

      // Update duration if it becomes finite later (common with WebM streams)
      if (video.duration && isFinite(video.duration) && video.duration !== this.videoDuration()) {
        this.videoDuration.set(video.duration);
      }
    }
  }

  onMetadataLoaded(event: Event) {
    const video = event.target as HTMLVideoElement;
    if (isFinite(video.duration)) {
      this.videoDuration.set(video.duration);
    } else {
      // Workaround for WebM Infinity duration bug
      // Force seek to a gigantic timestamp to force the browser to compute the end of the blob
      const originalTime = video.currentTime;
      video.currentTime = Number.MAX_SAFE_INTEGER;

      const onSeeked = () => {
        video.removeEventListener('seeked', onSeeked);
        const actualDuration = video.currentTime;
        video.currentTime = originalTime; // jump back

        if (isFinite(actualDuration) && actualDuration > 0) {
          this.videoDuration.set(actualDuration);
        }
      };

      video.addEventListener('seeked', onSeeked);
    }
  }

  onSeekPreview(event: Event) {
    this.isDraggingSlider = true;
    const input = event.target as HTMLInputElement;
    this.currentTime.set(parseFloat(input.value));

    if (this.lightboxVideo?.nativeElement) {
      this.wasPlayingBeforeSeek = !this.lightboxVideo.nativeElement.paused;
      this.lightboxVideo.nativeElement.pause();
    }
  }

  onSeekEnd(event: Event) {
    this.isDraggingSlider = false;
    const input = event.target as HTMLInputElement;
    const newTime = parseFloat(input.value);
    this.currentTime.set(newTime);

    if (this.lightboxVideo?.nativeElement) {
      this.lightboxVideo.nativeElement.currentTime = newTime;
      if (this.wasPlayingBeforeSeek) {
        this.lightboxVideo.nativeElement.play();
      }
    }
  }

  selectedEventFilter = 'ALL';

  get filteredAlertLogs() {
    return this.alertLogs.filter(log => {
      const matchSeverity = this.selectedSeverityFilter === 'ALL' || log.severity === this.selectedSeverityFilter;
      const matchEvent = this.selectedEventFilter === 'ALL' || log.event === this.selectedEventFilter;
      return matchSeverity && matchEvent;
    });
  }

  onEventFilterChange(event: any) {
    this.selectedEventFilter = event.target.value;
  }

  onSeverityFilterChange(event: any) {
    this.selectedSeverityFilter = event.target.value;
  }

  get uniqueEvents(): string[] {
    return Array.from(new Set(this.alertLogs.map((l: any) => l.event)));
  }

  get uniqueSeverities(): string[] {
    return Array.from(new Set(this.alertLogs.map((l: any) => l.severity)));
  }

  async downloadRecording(event: Event, url: string | null | undefined) {
    event.stopPropagation();
    event.preventDefault();
    if (!url) return;
    try {
      this.toast.info('Preparing download, please wait...');
      // Try to fetch to blob to force local download
      const response = await fetch(url, { mode: 'cors', referrerPolicy: 'no-referrer' });
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `recording_${this.evaluationData?.candidate_name?.replace(/\s+/g, '_') || 'interview'}.webm`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      this.toast.success('Download completed!');
    } catch (error) {
      window.open(url, '_blank');
    }
  }
}
